import React from 'react';

import {
  defaultI18nProvider,
  AdminContext,
  AdminUI,
  CustomRoutes,
  Notification,
  Authenticated,
  localStorageStore,
} from 'react-admin';

import { Route, BrowserRouter } from 'react-router-dom';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessIcon from '@mui/icons-material/Business';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import DescriptionIcon from '@mui/icons-material/Description';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import ExtensionIcon from '@mui/icons-material/Extension';
import HistoryIcon from '@mui/icons-material/History';
import InterestsIcon from '@mui/icons-material/Interests';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RouteIcon from '@mui/icons-material/Route';
import SellIcon from '@mui/icons-material/Sell';
import TuneIcon from '@mui/icons-material/Tune';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

import AuditList from './components/audit/AuditList';
import BusinessList from './components/business/BusinessList';
import BusinessEdit from './components/business/BusinessEdit';
import BusinessCreate from './components/business/BusinessCreate';
import CarList from './components/car/CarList';
import CarEdit from './components/car/CarEdit';
import CarCreate from './components/car/CarCreate';
import CaseList from './components/case/CaseList';
import CaseEdit from './components/case/CaseEdit';
import IndividualList from './components/individual/IndividualList';
import IndividualsEdit from './components/individual/IndividualEdit';
import IndividualCreate from './components/individual/IndividualCreate';
import PingList from './components/ping/PingList';
import PingEdit from './components/ping/PingEdit';
import ProductList from './components/product/ProductList';
import ProductsEdit from './components/product/ProductEdit';
import ProductCreate from './components/product/ProductCreate';
import RequestList from './components/request/RequestList';
import RequestCreate from './components/request/RequestCreate';
import RequestEdit from './components/request/RequestEdit';
import RuleCreate from './components/rules/RulesCreate';
import RuleList from './components/rules/RulesList';
import RuleEdit from './components/rules/RulesEdit';
import PreviousRuleReviewList from './components/rules/PreviousRuleReviewList';
import RiskClassList from './components/riskClass/RiskClassList';
import ReportList from './components/reports/ReportList';
import TagList from './components/tag/TagList';
import TagEdit from './components/tag/TagEdit';
import TagCreate from './components/tag/TagCreate';
import TransactionTypeList from './components/transactionType/TransactionTypeList';
import ProcessList from './components/process/ProcessList';
import Login from './components/authentication/login/Login';
import ResetPasswordPage from './components/authentication/resetpassword/ResetPasswordPage';
import ForgetPasswordPage from './components/authentication/forgetpassword/ForgetPasswordPage';
import MfaChallengePage from './components/authentication/login/mfa/MfaChallengePage';
import MfaForcedEnrollmentPage from './components/authentication/login/mfa/MfaForcedEnrollmentPage';
import MfaResetPage from './components/authentication/resetmfa/MfaResetPage';

import Dashboard from './components/dashboard/Dashboard';

import config from './config';
import Layout from './Layout';
import theme from './theme';

import AuthProvider from './utilities/Authprovider';
import preferenceKeepingStore from './utilities/preferenceKeepingStore';
import addUploadFeature from './utilities/dataProvider/addUploadFeature';
import AuthenticatedResource from './components/authentication/AuthenticatedResource';
import { SAR, StartPage, STR } from './components/fipo';

import AccountEntityList from './components/accountEntity/AccountEntityList';
import AccountEntityEdit from './components/accountEntity/AccountEntityEdit';
import getDataProvider from './utilities/dataProvider/getDataProvider';
import GroupList from './components/group/GroupList';
import SignalList from './components/signal/SignalList';
import queryClient from './utilities/queryClient';
import LogoutOnIdle from './components/utils/LogoutOnIdle';
import TransactionTableSetup from './components/transactionTableSetup/TransactionTableSetup';
import resourceNames from './constants/resourceNames';
import AccessManagement from './components/accessManagement/AccessManagement';
import SentryError from './SentryError';
import FormList from './components/form/FormList';
import FormEdit from './components/form/FormEdit';
import FormStudio from './components/form/FormStudio';
import SecurityPage from './components/security/SecurityPage';

const dataProvider = getDataProvider(config.apiUrl);
const uploadCapableDataProvider = addUploadFeature(dataProvider);
const store = preferenceKeepingStore(localStorageStore());

const App = () => (
  <BrowserRouter>
    <AdminContext
      dataProvider={uploadCapableDataProvider}
      theme={theme}
      queryClient={queryClient}
      authProvider={AuthProvider}
      store={store}
      i18nProvider={defaultI18nProvider}
    >
      <LogoutOnIdle />
      <AdminUI
        error={SentryError}
        title="Pingwire"
        layout={Layout}
        notification={Notification}
        loginPage={Login}
        dashboard={Dashboard}
        requireAuth
        disableTelemetry
      >
        <AuthenticatedResource
          name={resourceNames.ACCOUNT_ENTITIES}
          list={AccountEntityList}
          edit={AccountEntityEdit}
          icon={AccountBalanceIcon}
          options={{ label: 'Accounts' }}
        />
        <AuthenticatedResource
          name={resourceNames.BUSINESSES}
          list={BusinessList}
          edit={BusinessEdit}
          create={BusinessCreate}
          icon={BusinessIcon}
        />
        <AuthenticatedResource
          name={resourceNames.CARS}
          list={CarList}
          edit={CarEdit}
          create={CarCreate}
          icon={DriveEtaIcon}
        />
        <AuthenticatedResource
          name={resourceNames.INDIVIDUALS}
          list={IndividualList}
          edit={IndividualsEdit}
          create={IndividualCreate}
          icon={PersonPinIcon}
        />
        <AuthenticatedResource
          name={resourceNames.PRODUCTS}
          list={ProductList}
          edit={ProductsEdit}
          create={ProductCreate}
          icon={ExtensionIcon}
        />
        <AuthenticatedResource
          name={resourceNames.RISK_CLASSES}
          list={RiskClassList}
          icon={CrisisAlertIcon}
          options={{ label: 'Risk classes' }}
        />
        <AuthenticatedResource
          name={resourceNames.GROUPS}
          list={GroupList}
          icon={WorkspacesIcon}
        />
        <AuthenticatedResource
          name={resourceNames.TRANSACTION_TYPES}
          list={TransactionTypeList}
          icon={ReceiptIcon}
          options={{ label: 'Transaction types' }}
        />
        <AuthenticatedResource
          name={resourceNames.PROCESSES}
          list={ProcessList}
          icon={RouteIcon}
        />
        <AuthenticatedResource
          name={resourceNames.SIGNALS}
          list={SignalList}
          icon={NotificationsIcon}
        />
        <AuthenticatedResource
          name={resourceNames.CASES}
          list={CaseList}
          edit={CaseEdit}
          icon={DescriptionIcon}
        />
        <AuthenticatedResource
          name={resourceNames.PINGS}
          list={PingList}
          edit={PingEdit}
          icon={EmojiObjectsIcon}
        />
        <AuthenticatedResource
          name={resourceNames.REQUESTS}
          list={RequestList}
          edit={RequestEdit}
          create={RequestCreate}
          icon={DoubleArrowIcon}
        />
        <AuthenticatedResource
          name={resourceNames.AUDIT_TRACKS}
          list={AuditList}
          icon={HistoryIcon}
          options={{ label: 'Audit trail' }}
        />
        <AuthenticatedResource
          name={resourceNames.REPORTS}
          list={ReportList}
          icon={AssessmentIcon}
        />
        <AuthenticatedResource
          name={resourceNames.RULES}
          list={RuleList}
          edit={RuleEdit}
          create={RuleCreate}
          icon={TuneIcon}
        />
        <AuthenticatedResource
          name={resourceNames.TAGS}
          list={TagList}
          edit={TagEdit}
          create={TagCreate}
          icon={SellIcon}
        />
        <AuthenticatedResource
          name={resourceNames.SCORES}
        />
        <AuthenticatedResource
          name={resourceNames.FORM_ANSWERS}
        />
        <AuthenticatedResource
          name={resourceNames.FORMS}
          list={FormList}
          edit={FormEdit}
          icon={DynamicFormIcon}
        />
        <AuthenticatedResource
          name={resourceNames.BANK_DATA}
        />
        <AuthenticatedResource
          name={resourceNames.RULE_VERSION}
        />
        <AuthenticatedResource
          name={resourceNames.REVIEWS}
        />
        <AuthenticatedResource
          name={resourceNames.DATA_POINTS}
        />
        <AuthenticatedResource
          name={resourceNames.DATA_POINT_SOURCES}
        />
        <AuthenticatedResource
          name={resourceNames.CONNECTIONS}
        />
        <AuthenticatedResource
          name={resourceNames.INTERFACE_SETTINGS}
          list={TransactionTableSetup}
          icon={InterestsIcon}
          options={{ label: 'Interface settings' }}
        />
        <CustomRoutes noLayout>
          <Route path="/forget-password/:alias" element={<ForgetPasswordPage />} />
          <Route path="/login/mfa" element={<MfaChallengePage />} />
          <Route path="/login/mfa/enroll" element={<MfaForcedEnrollmentPage />} />
          <Route path="/set-password" element={<ResetPasswordPage />} />
          <Route path="/reset-mfa" element={<MfaResetPage />} />
        </CustomRoutes>
        <CustomRoutes noLayout>
          <Route path="/fipo/:caseId">
            <Route path="" element={<Authenticated><StartPage /></Authenticated>} />
            <Route path="str">
              <Route path=":path" element={<Authenticated><STR /></Authenticated>} />
            </Route>
            <Route path="sar">
              <Route path=":path" element={<Authenticated><SAR /></Authenticated>} />
            </Route>
          </Route>
        </CustomRoutes>
        <CustomRoutes>
          <Route
            path="/forms/:id/studio"
            element={<Authenticated><FormStudio /></Authenticated>}
          />
          <Route path="access-management">
            <Route index path="*" element={<Authenticated><AccessManagement /></Authenticated>} />
          </Route>
          <Route path="rules/reviews" element={<Authenticated><PreviousRuleReviewList /></Authenticated>} />
          <Route path="security" element={<Authenticated><SecurityPage /></Authenticated>} />
        </CustomRoutes>
      </AdminUI>
    </AdminContext>
  </BrowserRouter>
);

export default App;
